// Runtime feature entitlements for the logged-in user (frontend gating + upsell).
//
// Source of truth: the effective `features` array the backend adds to the login
// payload (stored on session `userData.features`; also fetchable via
// GET /api/v3/me/features). A user is shown the locked/upsell UI for any feature
// NOT in their set — regardless of backend soft/hard 403 enforcement (upsell is
// pure UX). GH Admins bypass. If we don't know a user's entitlements yet
// (no `features` on the session — e.g. an older login), we DON'T lock anything.
//
// A localStorage "preview" still overrides everything for testing/QA:
//   localStorage.gh_rbac_preview = '{"features":["inbox","contacts","templates.use"]}'
// (an empty features array locks everything; delete the key to clear.)

import { FEATURE_KEYS } from '../components/access-control/featureAccess';
import apiCore from './api-core';
import session from './session';
import config from '../config';

// Re-read the current token's effective features from the backend and store
// them on session.userData. Needed in two places:
//   1. after a brand / sub-company switch — sub-profiles come from
//      getSubUsersWithTokens, which does NOT include `features`, so without this
//      the session would fall back to "unknown" and lock nothing;
//   2. on app boot — so an admin's grant/revoke applies without a re-login.
// Fail-open on error (leaves whatever is already there), matching hasFeature's
// "entitlements unknown → don't lock" rule.
export async function refreshRuntimeFeatures() {
    try {
        const res = await apiCore.get('/api/v3/me/features');
        const features = res && res.data && res.data.features;
        if (!Array.isArray(features)) return null;
        session.set('userData', { ...(session.get('userData') || {}), features });
        return features;
    } catch {
        return null;
    }
}

function readPreview() {
    try {
        const raw = window.localStorage.getItem('gh_rbac_preview');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { features: Array.isArray(parsed.features) ? new Set(parsed.features) : null };
    } catch {
        return null;
    }
}

export function getRuntimeFeatures() {
    const preview = readPreview();
    const userData = session.get('userData') || {};
    const isGhAdmin = (userData.role || '') === config.userRoles.ghAdmin;

    // Effective feature set: preview (QA) wins, else the login-payload features.
    // null = unknown → never lock.
    let featureSet = null;
    const previewing = !!(preview && preview.features);
    if (previewing) featureSet = preview.features;
    else if (Array.isArray(userData.features)) featureSet = new Set(userData.features);

    const hasFeature = (key) => {
        if (!key) return true; // untagged surface → always available
        if (isGhAdmin && !previewing) return true; // GH Admin bypass (preview can force locks to QA them)
        if (!featureSet) return true; // entitlements unknown → don't lock
        return featureSet.has(key);
    };

    return {
        isGhAdmin,
        known: !!featureSet,
        hasFeature,
        features: featureSet ? [...featureSet] : FEATURE_KEYS.slice(),
    };
}
